import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {Icon} from '@wandb/weave/components/Icon';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {
  FC,
  useCallback,
  // @ts-ignore We're actually on React 18 which includes the useDeferredValue hook
  useDeferredValue,
} from 'react';

import {useStablePanelId} from '../Panel/PanelIDContext';
import {InstrumentedLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {
  CellDimensionsSelector,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_ROW_HEIGHT,
} from './components/CellDimensionsSelector';
import DiffToggleNew from './components/DiffToggleNew';
import {SearchConfigs} from './components/SearchConfigs';
import {isValidRegex, StickyTreeWrapper} from './components/StickyTreeWrapper';
import {
  RunComparerContextProvider,
  useRunComparer,
  useRunComparerContext,
} from './RunComparerContextProvider';
import type {RunComparerConfig} from './types';
import {PanelRunComparerProps} from './types';
import {allLeafNodesEqual} from './utils/allLeafNodesEqual';
import {useRunComparerTree} from './utils/useRunComparerTree';

const ComponentStickyVirtualizedTree: FC<PanelRunComparerProps> = props => {
  const {
    config: {customRowHeight, customColumnWidth},
    updateConfig,
  } = props;
  const {localSearchString} = useRunComparerContext();
  const rowHeight = customRowHeight ?? DEFAULT_ROW_HEIGHT;
  const columnWidth = customColumnWidth ?? DEFAULT_COLUMN_WIDTH;

  const {tree, loading} = useRunComparerTree(
    {
      skip: !isValidRegex(localSearchString), // skip building the tree and checking for matches if the search string is not a valid regex
      loading: props.loading,
      runs: props.data.filtered,
      keyInfo: props.data.keyInfo,
      diffOnly: props.config.diffOnly,
      searchString: localSearchString, // use local saved search string not input search string to avoid re-constructing the tree on every keystroke
    },
    250000 // This is about where we start noticing sluggishness in rendering the tree
  );
  // Using useDeferredValue for the tree data prevents it from blocking the main thread when the tree is being rendered.
  // https://react.dev/reference/react/useDeferredValue#deferring-re-rendering-for-a-part-of-the-ui
  const fullTree = useDeferredValue(tree);

  const runComparerRef = React.useRef<HTMLDivElement>(null);

  const hasNoDiff = useCallback(() => {
    return allLeafNodesEqual(fullTree);
  }, [fullTree]);

  // We delay the update to the config by a tick so that the various settings can update their UI immediately without
  // waiting for the tree to rebuild.
  const updateConfigDelayed = useCallback(
    (config: Partial<RunComparerConfig>) => {
      setTimeout(() => {
        updateConfig(config);
      }, 0);
    },
    [updateConfig]
  );
  const setDiffOnly = useCallback(
    (diffOnly: boolean) => {
      updateConfigDelayed({diffOnly});
    },
    [updateConfigDelayed]
  );
  const setRowHeight = useCallback(
    (newRowHeight: number) => {
      updateConfigDelayed({customRowHeight: newRowHeight});
    },
    [updateConfigDelayed]
  );
  const setColumnWidth = useCallback(
    (newColumnWidth: number) => {
      updateConfigDelayed({customColumnWidth: newColumnWidth});
    },
    [updateConfigDelayed]
  );

  const stablePanelId = useStablePanelId();

  if (props.loading || loading) {
    return <Loader name="panel-run-loader" />;
  }

  if (props.data.filtered.length === 0) {
    return (
      <PanelError
        panelID={stablePanelId}
        message="Select at least one run to load data into this run comparer."
      />
    );
  }

  if (Object.keys(fullTree).length === 0 && localSearchString == null) {
    return (
      <PanelError
        panelID={stablePanelId}
        message="No keys found. This is probably an error."
      />
    );
  }

  if (props.config.diffOnly && hasNoDiff() && localSearchString == null) {
    return (
      <PanelError
        panelID={stablePanelId}
        panelViewType="Run Comparer"
        message="There are no differences between the selected runs."
      />
    );
  }

  return (
    <TailwindContents>
      <div className="run-comparer-wrapper-new h-full w-full">
        <div className="run-comparer-new h-full w-full" ref={runComparerRef}>
          <div className="mt-10 flex w-full flex-wrap gap-y-2 bg-white">
            <div className="w-full min-w-[150px] max-w-[calc(100%-360px)]">
              {/*
                This may not be the best way to do this in CSS, but the intended behavior is:
                - Header wraps components if panel is small
                - If panel is wide enough, diff toggle and dimension selector should be on same line
                - Search has a minimum size
                - Search has max size of (full width - space for diff toggle and dimension selector)
              */}
              <SearchConfigs />
            </div>
            {props.data.filtered.length > 1 && (
              <div className="flex h-32 w-[100px]">
                <DiffToggleNew
                  diffOnly={props.config.diffOnly}
                  className="!h-32"
                  onChange={setDiffOnly}
                />
              </div>
            )}
            <CellDimensionsSelector
              rowHeight={rowHeight}
              columnWidth={columnWidth}
              setRowHeight={setRowHeight}
              setColumnWidth={setColumnWidth}
            />
          </div>
          <div className="h-full w-full overflow-hidden">
            {Object.keys(fullTree).length === 0 && localSearchString != null ? (
              <div className="flex h-full items-center justify-center">
                <div className="flex flex-col items-center">
                  <Icon name="info" className="mr-4 h-24 w-24" />
                  <div
                    className="mt-8 text-moon-800"
                    data-test="run-comparer-no-matching-keys">
                    {isValidRegex(localSearchString)
                      ? 'No matching keys found.'
                      : 'Invalid regex search'}
                  </div>
                </div>
              </div>
            ) : (
              <StickyTreeWrapper {...props} />
            )}
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};

const ComponentWrapper = (props: PanelRunComparerProps) => {
  const contextValue = useRunComparer(props);
  return (
    <RunComparerContextProvider contextValue={contextValue}>
      <ComponentStickyVirtualizedTree {...props} />
    </RunComparerContextProvider>
  );
};

export default ComponentWrapper;
